/**
 * Voice Agent API service module
 * Handles CRUD operations for end-to-end voice agents
 */
import { request } from '@koi-design/vix-tools';
import { packApi } from '@/libs/tools';

function getHeaders(data) {
  const headers = {};
  if (data?.cybertron_robot_key) {
    headers['cybertron-robot-key'] = data.cybertron_robot_key;
  }
  if (data?.cybertron_robot_token) {
    headers['cybertron-robot-token'] = data.cybertron_robot_token;
  }
  return headers;
}

/**
 * Create a new voice agent
 * @param {Object} data - Voice agent data
 * @returns {Promise}
 */
export const createVoiceAgentApi = data => {
  return request({
    url: '/ee-robot/api/v1/robot/create-base-config',
    method: 'post',
    data
  });
};

/**
 * Get voice agent base config
 * @param {Object} data - { robot_id: string, robot_type: string, homeland_id: string, source: string }
 * @returns {Promise}
 */
export const getVoiceAgentBaseConfigApi = data => {
  return request({
    url: `/ee-robot/api/v1/get_robot_config`,
    method: 'get',
    data
  });
};

/**
 * Update voice agent configuration
 * @param {Object} data - Voice agent data with id
 * @returns {Promise}
 */
export const updateVoiceAgentApi = data => {
  return request({
    url: `/ee-robot/api/robot/update_config/`,
    method: 'post',
    data
  });
};

/**
 * Delete a voice agent
 * @param {Object} data - { id: string, homeland_id: string }
 * @returns {Promise}
 */
export const deleteVoiceAgentApi = data => {
  return packApi({
    url: '/api/api-robot/voice-agent/delete/',
    method: 'post',
    data
  });
};

/**
 * Get available models for a provider
 * @param {Object} data - { provider: string }
 * @returns {Promise}
 */
export const getVoiceModelListApi = data => {
  return request({
    url: '/ee-robot/api/v1/llm/list',
    method: 'get',
    data
  });
};

/**
 * Get conversation segment history
 * @param {Object} data - { robot_id: string, page: number, pagesize: number, segment_code: string, mode: number, order: number, message_source: string }
 * @returns {Promise}
 */
export const getConversationSegmentHistoryApi = data => {
  return request({
    url: '/ee-robot/api/v1/conversation/get_conversation_segment_history/',
    method: 'get',
    data,
    headers: getHeaders(data)
  });
};

/**
 * Reply detail for a single message (same contract as cybotstar get_reply_logic)
 * @param {Object} data - { message_id: string|number, message_source?: string }
 */
export const getVoiceAgentReplyLogicApi = data => {
  return request({
    url: '/ee-robot/api/v1/conversation/get_reply_logic/',
    method: 'get',
    data,
    headers: getHeaders(data)
  });
};

export const getConversationSegmentListApi = data => {
  return request({
    url: '/ee-robot/api/v1/conversation/get_conversation_segment_list/',
    method: 'get',
    data
  });
};

/**
 * Voice agent history list adapter.
 * Normalize list API output to match usePageList shape.
 */
export const getVoiceConversationHistoryListApi = async data => {
  const { start_timestamp, end_timestamp, ...rest } = data;
  const requestData = {
    start_time: start_timestamp,
    end_time: end_timestamp,
    ...rest
  };
  return request({
    url: '/ee-robot/api/v1/conversation/segment/get_detail_list/',
    method: 'get',
    data: requestData
  });
};

export const getPromptTemplateApi = () => {
  return request({
    url: '/ee-robot/api/v1/robot/prompt/',
    method: 'get'
  });
};

/**
 * Get LLM result for voice agent prompt optimization.
 * @param {Object} data - { request_type: number, user_text: string }
 * @returns {Promise<{ llm_answer: string }>}
 */
export const getLlmResultApi = data => {
  return request({
    url: '/ee-robot/api/v1/llm/get_llm_result',
    method: 'post',
    data,
    timeout: 60000
  });
};

export const getVoiceAgentChatLogApi = data => {
  return request({
    url: '/ee-robot/api/v1/traces/hierarchy-list/',
    method: 'get',
    data
  });
};

export const getVoiceAgentChatLogDetailApi = data => {
  return request({
    url: '/ee-robot/api/v1/traces/span/detail/',
    method: 'get',
    data
  });
};

export const getVoiceAgentDraftReleaseInfoApi = data => {
  return request({
    url: '/ee-robot/api/v1/robot/draft_release_info/',
    method: 'post',
    data
  });
};

export const getVoiceAgentNextReleaseVersionApi = data => {
  return request({
    url: '/ee-robot/api/v1/robot/release/next_version/',
    method: 'get',
    data
  });
};

export const releaseVoiceAgentApi = data => {
  return request({
    url: '/ee-robot/api/v1/robot/release/new',
    method: 'post',
    data
  });
};

export const deleteVoiceAgentDraftApi = data => {
  return packApi({
    url: `/ee-robot/api/v1/robot/draft`,
    method: 'delete',
    data
  });
};

export const offlineVoiceAgentApi = data => {
  return packApi({
    url: '/ee-robot/api/v1/robot/release/offline',
    method: 'post',
    data
  });
};

export const getVoiceAgentKeyApi = data => {
  return request({
    url: '/ee-robot/api/v1/robot/robot-key/',
    method: 'post',
    data
  });
};

export const getVoiceAgentSecretKeyApi = data => {
  return request({
    url: `/ee-robot/api/v1/third-party/secret-key`,
    method: 'get',
    data
  });
};

export const getVoiceAgentRobotTokenApi = data => {
  return request({
    url: `/ee-robot/api/v1/third-party/robot-token`,
    method: 'post',
    data
  });
};

export const getVoiceAgentInfoBySecretIdApi = data => {
  return request({
    url: '/ee-robot/openapi/v1/gw/robot/info',
    method: 'get',
    data,
    catchAll: true
  });
};

// for edit page & usage page
export const createSessionApi = data => {
  return request({
    url: '/ee-robot/api/segment/get_conversation_segment_code/',
    method: 'get',
    data
  });
};

export const validateDraftApi = data => {
  return request({
    url: '/ee-robot/api/v1/robot/validate_draft/',
    method: 'post',
    data
  });
};

// for share page
export const createShareSessionApi = data => {
  return request({
    url: '/ee-robot/openapi/api-conversation/segment_share/get_conversation_segment_code_share',
    method: 'post',
    data,
    headers: getHeaders(data)
  });
};

export const getShareLiveToken = data => {
  return request({
    url: '/ee-robot/openapi/v1/gw/live/get_live_token',
    method: 'post',
    data,
    catchAll: true
  });
};

export const getShareConversationSegmentListApi = data => {
  return request({
    url: '/ee-robot/openapi/api-conversation/segment_share/get_conversation_segment_list_share/',
    method: 'post',
    data
  });
};

export const getLastSegmentCodeApi = data => {
  return request({
    url: '/ee-robot/api/v1/robot/conversation/get_last_segment_code/',
    method: 'get',
    data
  });
};

export const getLastReleaseVersionApi = data => {
  return request({
    url: '/ee-robot/api/v1/robot/release/latest',
    method: 'get',
    data
  });
};

export const updatePlatformsApi = data => {
  return request({
    url: '/ee-robot/api/v1/robot/release/update_platforms',
    method: 'post',
    data
  });
};

/**
 * Get all voiceprint groups for dropdown / manual association
 * @param {Object} data - { homeland_id: number|string, query?: string }
 * @returns {Promise<Array<{id, name, description, ...}>>}
 */
export const getVoiceprintGroupAllApi = data => {
  return request({
    url: '/ee-robot/api/v1/voiceprint/group/all',
    method: 'get',
    data
  });
};

/**
 * Voice agent phase3: fetch available knowledge bases for resource config.
 * Note: backend endpoint may change; keep usage isolated in voice-agent domain.
 */
export const getVoiceAgentKnowledgeListApi = data => {
  return request({
    url: '/ee-robot/api/v1/knowledge/base/available',
    method: 'get',
    data
  });
};

/**
 * Voice agent phase3: fetch variable definitions (system + custom).
 * Note: kept as a dedicated API wrapper for forward compatibility.
 */
export const getVoiceAgentVariableListApi = data => {
  return request({
    url: '/ee-robot/api/v1/robot/variables',
    method: 'get',
    data
  });
};

export const isInWhiteListApi = data => {
  return request({
    url: '/ee-robot/api/v1/user/is-in-white',
    method: 'get',
    data
  });
};

export const postWebcallCopilotUploadApi = (data, query, headers = {}) => {
  const queryString = Object.entries(query)
    .map(([key, value]) => `${key}=${value}`)
    .join('&');
    // const copilotUploadUrl = /-pre\d\.|localhost/i.test(window.location.host) ? '192.168.160.65:18081/voice_server' : `${window.location.host}`;
    const copilotUploadUrl = `${window.location.host}`;
  return request({
    url: `https://${copilotUploadUrl}/ee-robot/api/v1/copilot/upload?${queryString}`,
    method: 'post',
    data,
    headers,
    catchAll: true
  });
};
